export type Approver = "pc" | "bharat";
export type DecisionValue = "approved" | "changes_requested";
export type PimStatus = "uploaded" | "draft" | "in_review" | "changes_requested" | "approved" | "published" | "withdrawn" | "archived";

export interface PimRow {
  id: number;
  companyName: string;
  slug: string;
  status: PimStatus;
  currentVersionId: number | null;
  publishedVersionId: number | null;
  publishedAt: string | null;
  withdrawnAt: string | null;
  withdrawalReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SourceItem {
  label?: string;
  title?: string;
  url?: string;
  page?: number;
  pageNumbers?: number[];
  text?: string;
}

export interface PimVersion {
  id: number;
  pimId: number;
  version: number;
  contentHash: string;
  sourceObjectPath: string;
  sourceFilename: string;
  sourceChecksum: string;
  mimeType: string;
  fileSize: number;
  pageCount: number;
  processingStatus: "uploaded" | "processing" | "draft" | "failed";
  processingError: string | null;
  sector: string | null;
  geography: string | null;
  stage: string | null;
  leadAdvisor: string | null;
  preparedDate: string | null;
  evaluationStatus: string;
  disclosureVersion: string;
  cohortIds: number[];
  cardSnippet: string | null;
  campaignSnippet: string | null;
  executiveSummary: string | null;
  evaluationThesis: string | null;
  researchThemes: unknown[];
  claims: SourceItem[];
  evidence: SourceItem[];
  evidenceGaps: SourceItem[];
  risks: SourceItem[];
  sources: SourceItem[];
  lowConfidenceFields: string[];
  estimatedReadMinutes: number | null;
  claimsEvidenceConfirmed: boolean;
  submittedAt: string | null;
  createdAt: string;
}

export interface PimDecision {
  id: number;
  versionId: number;
  approver: Approver;
  decision: DecisionValue;
  comment: string | null;
  decidedBy: number;
  decidedAt: string;
}

export interface PimGrant {
  id: number;
  pimId: number;
  versionId: number;
  cohortId: number;
  policyId: number;
  active: boolean;
  grantedAt: string;
  revokedAt: string | null;
}

export interface PimListRecord {
  pim: PimRow;
  version: PimVersion | null;
  approvals: Record<Approver, PimDecision | null>;
}

/** Actual admin response is intentionally flattened for the editorial UI. */
export interface AdminPimListItem {
  id: string; companyName: string; sector: string | null; geography: string | null;
  version: number; status: PimStatus; processingMessage: string | null; updatedAt: string;
  preparedDate: string | null; cohorts: Array<{ id: string; name: string }>;
  approvals: Record<Approver, "pending" | DecisionValue>; campaignEligible: boolean;
}
export interface AdminPimListResponse { items: AdminPimListItem[]; cohorts: Array<{ id: string; name: string; activeInvestorCount: number; researchEnabled: boolean; campaignEnabled: boolean }> }
export interface AdminPimDetail extends AdminPimListItem {
  slug: string; stage: string | null; leadAdvisor: string | null; canApproveResearchPolicies: boolean; cardSnippet: string;
  executiveSummary: string; evaluationStatus: string; campaignSnippet: string; disclosureVersion: string;
  claimsEvidenceConfirmed: boolean;
  source: { filename: string; checksum: string; mimeType: string; pageCount: number; fileSize: number };
  chapters: Array<{ key: string; title: string; body: string; citations: Array<{ id?: string; label: string; url?: string; page?: number | null }> }>;
  availableCohorts: AdminPimListResponse["cohorts"]; selectedCohortIds: string[];
  entitlementPreview: { uniqueInvestorCount: number; cohorts: Array<{ id: string; name: string; investorCount: number; alreadyEntitledCount: number }>; exclusions: Array<{ reason: string; count: number }> };
  publicationReadiness: PublicationReadiness[];
  approvalHistory: Array<{ id: string; approver: Approver; decision: DecisionValue; comment: string | null; version: number; actorName: string; createdAt: string }>;
  priorCohortEnablement: unknown;
  campaign: { id: string; status: CampaignRow["status"]; subject: string; preheader: string; introduction: string; pimIds: string[]; cohortIds: string[]; excludedRecipientIds: string[]; recipientCount: number; exclusionCount: number; approvals: Record<Approver, "pending" | DecisionValue>; sentAt: string | null } | null;
}

export interface PublicationReadiness {
  cohortId: string;
  cohortName: string;
  ready: boolean;
  state: "ready" | "missing" | "disabled" | "awaiting_approval" | "scheduled" | "expired";
  activePolicyId: number | null;
  activeRevision: number | null;
  activeAccessMode: CohortPolicyInput["accessMode"] | null;
  latestPolicyId: number | null;
  latestRevision: number | null;
}

export function mapAdminList(response: AdminPimListResponse): { items: PimListRecord[]; cohorts: Cohort[] } {
  const cohorts = response.cohorts.map((c) => ({ id: Number(c.id), name: c.name }));
  return {
    cohorts,
    items: response.items.map((item) => ({
      pim: { id: Number(item.id), companyName: item.companyName, slug: "", status: item.status, currentVersionId: null, publishedVersionId: null, publishedAt: null, withdrawnAt: null, withdrawalReason: null, createdAt: item.updatedAt, updatedAt: item.updatedAt },
      version: { id: 0, pimId: Number(item.id), version: item.version, contentHash: "", sourceObjectPath: "", sourceFilename: "", sourceChecksum: "", mimeType: "application/pdf", fileSize: 0, pageCount: 0, processingStatus: item.status === "uploaded" ? "uploaded" : "draft", processingError: item.processingMessage, sector: item.sector, geography: item.geography, stage: null, leadAdvisor: null, preparedDate: item.preparedDate, evaluationStatus: "under_evaluation", disclosureVersion: "", cohortIds: item.cohorts.map((c) => Number(c.id)), cardSnippet: null, campaignSnippet: null, executiveSummary: null, evaluationThesis: null, researchThemes: [], claims: [], evidence: [], evidenceGaps: [], risks: [], sources: [], lowConfidenceFields: [], estimatedReadMinutes: null, claimsEvidenceConfirmed: false, submittedAt: null, createdAt: item.updatedAt },
      approvals: (["pc", "bharat"] as const).reduce((acc, who) => ({ ...acc, [who]: item.approvals[who] === "pending" ? null : { id: 0, versionId: 0, approver: who, decision: item.approvals[who] as DecisionValue, comment: null, decidedBy: 0, decidedAt: item.updatedAt } }), {} as Record<Approver, PimDecision | null>),
    })),
  };
}

export function mapAdminDetail(detail: AdminPimDetail): PimDetailRecord {
  const chapter = (key: string) => detail.chapters.find((c) => c.key === key);
  const sourceItems = (key: string): SourceItem[] => {
    const value = chapter(key);
    return value ? [{ body: value.body, citations: value.citations } as SourceItem] : [];
  };
  const version: PimVersion = {
    id: 0, pimId: Number(detail.id), version: detail.version, contentHash: "", sourceObjectPath: "", sourceFilename: detail.source.filename, sourceChecksum: detail.source.checksum, mimeType: detail.source.mimeType, fileSize: detail.source.fileSize, pageCount: detail.source.pageCount,
    processingStatus: detail.status === "uploaded" ? "uploaded" : "draft", processingError: detail.processingMessage,
    sector: detail.sector, geography: detail.geography, stage: detail.stage, leadAdvisor: detail.leadAdvisor, preparedDate: detail.preparedDate,
    evaluationStatus: detail.evaluationStatus, disclosureVersion: detail.disclosureVersion, cohortIds: detail.selectedCohortIds.map(Number),
    cardSnippet: detail.cardSnippet, campaignSnippet: detail.campaignSnippet, executiveSummary: detail.executiveSummary,
    evaluationThesis: chapter("pipeline_reason")?.body || "", researchThemes: [], claims: sourceItems("company_claims"), evidence: sourceItems("evidence_reviewed"), evidenceGaps: sourceItems("evidence_gaps"), risks: sourceItems("risks"), sources: sourceItems("sources"), lowConfidenceFields: [], estimatedReadMinutes: null, claimsEvidenceConfirmed: detail.claimsEvidenceConfirmed, submittedAt: null, createdAt: detail.updatedAt,
  };
  const approvals = (["pc", "bharat"] as const).reduce((acc, who) => {
    const event = detail.approvalHistory.find((e) => e.approver === who && e.version === detail.version);
    return { ...acc, [who]: event ? { id: Number(event.id), versionId: 0, approver: who, decision: event.decision, comment: event.comment, decidedBy: 0, decidedAt: event.createdAt } : null };
  }, {} as Record<Approver, PimDecision | null>);
  return { pim: { id: Number(detail.id), companyName: detail.companyName, slug: detail.slug, status: detail.status, currentVersionId: null, publishedVersionId: null, publishedAt: null, withdrawnAt: null, withdrawalReason: null, createdAt: detail.updatedAt, updatedAt: detail.updatedAt }, version, versions: [version], grants: [], events: [], approvals, publicationReadiness: detail.publicationReadiness, canApproveResearchPolicies: detail.canApproveResearchPolicies };
}

export interface PimDetailRecord {
  pim: PimRow;
  version: PimVersion;
  versions: PimVersion[];
  grants: PimGrant[];
  events: Array<{ id: number; event: string; cohortId: number | null; occurredAt: string }>;
  approvals: Record<Approver, PimDecision | null>;
  publicationReadiness: PublicationReadiness[];
  canApproveResearchPolicies: boolean;
}

export interface Cohort {
  id: number;
  name: string;
  slug?: string;
  status?: string;
}

export interface UploadHandshake {
  uploadURL: string;
  objectPath: string;
  method: "PUT";
  headers: { "Content-Type": "application/pdf" };
  expiresInSeconds: number;
}

export interface CohortPolicyInput {
  cohortId: number;
  enabled: boolean;
  activationDate: string;
  accessMode: "future_only" | "selected_back_catalogue" | "all_approved_historical";
  selectedPimIds: number[];
  campaignEnabled: boolean;
  disclosureVersion: string;
  accessEndsAt: string | null;
}

export function safeInlinePolicyInput(
  cohortId: number,
  disclosureVersion: string,
  activationDate: string,
): CohortPolicyInput {
  return {
    cohortId,
    enabled: true,
    activationDate,
    accessMode: "future_only",
    selectedPimIds: [],
    campaignEnabled: false,
    disclosureVersion,
    accessEndsAt: null,
  };
}

export interface CohortPolicy extends CohortPolicyInput {
  id: number;
  revision: number;
  status: "draft" | "active" | "changes_requested" | "superseded";
  policyHash: string;
  activatedAt: string | null;
  createdAt: string;
}

export interface PolicyPreview {
  cohortId: number;
  accessMode: CohortPolicyInput["accessMode"];
  visiblePimIds: number[];
  activeInvestorCount: number;
  historicalCampaignCount: number;
  campaignEnrollment: boolean;
  disclosureVersion: string | null;
}

export interface CampaignRow {
  id: number;
  status: "draft" | "in_review" | "changes_requested" | "approved" | "dispatch_ready" | "sent";
  subject: string;
  preheader: string | null;
  context: string;
  footerDisclosure: string;
  pimVersionIds: number[];
  cohortIds: number[];
  includedInvestorIds: number[];
  excludedInvestorIds: number[];
  contentHash: string;
  recipientHash: string;
  revision: number;
  idempotencyKey: string;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignDecision extends PimDecision {
  campaignId: number;
  revision: number;
  contentHash: string;
  recipientHash: string;
}

export interface CampaignDetail {
  campaign: CampaignRow;
  decisions: CampaignDecision[];
  deliveries: Array<{ id: number; investorId: number; status: string; failureCode: string | null }>;
}

type Envelope<T> = { data: T; meta?: unknown };
const token = () => localStorage.getItem("admin_token") || "";

export async function apiData<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      "x-admin-token": token(),
      ...(init?.headers || {}),
    },
  });
  const payload = await response.json().catch(() => null) as Envelope<T> | { error?: { message?: string } } | null;
  if (!response.ok) {
    const message = payload && "error" in payload ? payload.error?.message : null;
    throw new Error(message || `Request failed (${response.status})`);
  }
  if (!payload || !("data" in payload)) throw new Error("The server returned an invalid response envelope.");
  return payload.data;
}

export const researchData = <T>(path: string, init?: RequestInit) =>
  apiData<T>(`/api/admin/research-pipeline${path}`, init);

export async function putSignedPdf(handshake: UploadHandshake, file: File): Promise<void> {
  const response = await fetch(handshake.uploadURL, {
    method: handshake.method,
    headers: handshake.headers,
    body: file,
  });
  if (!response.ok) throw new Error(`Private PDF upload failed (${response.status}).`);
}